import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { identifyUser, resetUser } from '@/lib/analytics';

interface User {
  id: number;
  email: string;
  name: string;
  avatar: string;
  plan: string;
  mcp_enabled: boolean;
  default_customer_id: string | null;
  default_customer_name: string | null;
  google_ads_connected: boolean;
  chat_count: number;
  has_stripe: boolean;
  subscription_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  chatgpt_authenticated: boolean;
  chatgpt_active: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const res = await fetch('/api/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        if (data.user) {
          identifyUser(data.user.email, {
            name: data.user.name,
            plan: data.user.plan,
            user_id: data.user.id,
          });
        }
      } else {
        setUser(null);
        resetUser();
      }
    } catch {
      setUser(null);
      resetUser();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
